import type { AttributeType } from "@aws-sdk/client-cognito-identity-provider";
import type { AuthUser } from "./auth-user";

const nameMappings = [
	{ profileName: "firstName", attributeName: "given_name" },
	{ profileName: "middleName", attributeName: "middle_name" },
	{ profileName: "lastName", attributeName: "family_name" },
	{ profileName: "phoneNumber", attributeName: "phone_number" },
	{ profileName: "companyName", attributeName: "custom:company_name" },
	{ profileName: "country", attributeName: "custom:country" },
	{ profileName: "howHeard", attributeName: "custom:how_heard" },
	{ profileName: "emailAddress", attributeName: "email" },
	{ profileName: "birthdate", attributeName: "birthdate" },
	{ profileName: "originSite", attributeName: "custom:origin_site" },
	{ profileName: "currentSite", attributeName: "custom:current_site" },
	{ profileName: "ipAddress", attributeName: "custom:ip_address" },
	{
		profileName: "ipAddressCountry",
		attributeName: "custom:ip_address_country",
	},
] as const satisfies readonly {
	profileName: keyof AuthUser;
	attributeName: string;
}[];

type AuthUserAttributes = Pick<
	AuthUser,
	(typeof nameMappings)[number]["profileName"]
>;

type ProfileName = (typeof nameMappings)[number]["profileName"];

const profileNameByAttribute = new Map<string, ProfileName>(
	nameMappings.map((mapping) => [mapping.attributeName, mapping.profileName]),
);

// Reverse of profileToCognitoAttributes: turn a Cognito GetUser attribute list
// into a profile object, keeping only attributes we map. Used to read the latest
// attributes straight from Cognito rather than from the (growing) id token.
function cognitoAttributesToProfile(
	attributes: readonly AttributeType[],
): Partial<AuthUserAttributes> {
	const profile: Partial<Record<ProfileName, string>> = {};
	for (const { Name, Value } of attributes) {
		if (Name === undefined || Value === undefined) {
			continue;
		}
		const profileName = profileNameByAttribute.get(Name);
		if (profileName !== undefined) {
			profile[profileName] = Value;
		}
	}
	return profile;
}

function profileToCognitoAttributes(profile: Partial<AuthUserAttributes>) {
	return nameMappings
		.filter((mapping) => mapping.profileName in profile)
		.map(
			(mapping) =>
				[mapping.attributeName, profile[mapping.profileName]] as const,
		)
		.filter(([, value]) => value !== undefined && value !== null)
		.map(([attrName, value]) => ({
			Name: attrName,
			Value: value?.toString(),
		}));
}

export type { AuthUserAttributes };
export { profileToCognitoAttributes, cognitoAttributesToProfile };
